import { useEffect, useState, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

export type InstallStatus =
  | "available"      // beforeinstallprompt fired; user can tap our button
  | "installed"      // already running as a PWA / app installed event fired
  | "ios"            // iOS Safari — needs manual "Add to Home Screen"
  | "unsupported";   // browser doesn't support install (e.g., desktop Firefox)

function isStandalone(): boolean {
  // Chrome / Android use display-mode media; iOS uses navigator.standalone.
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // @ts-expect-error legacy iOS API
  if (window.navigator.standalone) return true;
  return false;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPad on iOS 13+ reports as MacIntel; check touch points to disambiguate.
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadOS;
}

export function useInstallPrompt(): {
  status: InstallStatus;
  install: () => Promise<"accepted" | "dismissed" | "unavailable">;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [status, setStatus] = useState<InstallStatus>(() => {
    if (isStandalone()) return "installed";
    if (isIos()) return "ios";
    return "unsupported";
  });

  useEffect(() => {
    if (status === "installed") return;
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setStatus("available");
    };
    const onAppInstalled = () => {
      setDeferred(null);
      setStatus("installed");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [status]);

  const install = useCallback(async () => {
    if (!deferred) return "unavailable" as const;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === "accepted") setStatus("installed");
    return choice.outcome;
  }, [deferred]);

  return { status, install };
}
